import { Component, EventEmitter, Input, Output } from '@angular/core';
import { faSave } from '@fortawesome/free-regular-svg-icons/faSave';
import { faEraser } from '@fortawesome/free-solid-svg-icons/faEraser';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { BehaviorSubject, mergeMap, Observable, of, skip, take, throwError } from 'rxjs';
import { InvitationInterface } from '../../../models/interface/invitation.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { HttpResponse } from '@angular/common/http';

type ResponseState = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

@Component({
  selector: 'app-card-pending-invitation',
  templateUrl: './card-pending-invitation.component.html',
  styleUrls: ['./card-pending-invitation.component.css'],
  providers: [StudyStore],
})
export class CardPendingInvitationComponent {
  @Output()
  public emitDelete = new EventEmitter<void>();

  @Output()
  public emitUpdate = new EventEmitter<void>();

  @Output()
  public emitStudyCodeUpdate = new EventEmitter<string | null>();

  // Icons
  faRedo = faRedo;
  faTrash = faTrash;
  faEraser = faEraser;
  faSave = faSave;

  public invitation: InvitationInterface;
  public studyCode: string;
  public isManager: boolean;
  public isECoach: boolean;
  public studies: StudyInterface[] = [];

  public currentDate = Math.round(Date.now() / 1000);

  // Response
  public addUpdateStudyCodeResponse = new BehaviorSubject<ResponseState>('DEFAULT');
  public deleteStudyCodeResponse = new BehaviorSubject<ResponseState>('DEFAULT');
  public resendStudyInvitationResponse = new BehaviorSubject<ResponseState>('DEFAULT');
  public deleteStudyInvitationResponse = new BehaviorSubject<ResponseState>('DEFAULT');

  private addMembersResponse$: Observable<any>;

  private updateMemberCodeResponse$: Observable<any>;
  private deleteMemberCodeResponse$: Observable<any>;
  private deleteStudyInvitationResponse$: Observable<any>;

  constructor(private helperService: HelperService, private studyStore: StudyStore) {
    this.addMembersResponse$ = this.studyStore.addMembersResponse$;
    this.updateMemberCodeResponse$ = this.studyStore.updateMemberCodeResponse$;
    this.deleteMemberCodeResponse$ = this.studyStore.deleteMemberCodeResponse$;
    this.deleteStudyInvitationResponse$ = this.studyStore.deleteStudyInvitationResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  @Input()
  set _invitation(_invitation: InvitationInterface) {
    if (_invitation) {
      this.invitation = _invitation;
    }
  }

  @Input()
  set _studyCode(_studyCode: string) {
    if (_studyCode) {
      this.studyCode = _studyCode;
    } else {
      this.studyCode = '';
    }
  }

  @Input()
  set _isManager(_isManager: boolean) {
    this.isManager = !!_isManager;
  }

  @Input()
  set _isECoach(_isEcoach: boolean) {
    this.isECoach = !!_isEcoach;
  }

  @Input()
  set _setStudies(_setStudies: StudyInterface[]) {
    if (this.studies) {
      this.studies = _setStudies;
    } else {
      this.studies = [];
    }
  }

  disableUpdateStudyCode(invitation: InvitationInterface, studyCode: string) {
    return (!studyCode && !invitation.attributes.code) || invitation.attributes.code === studyCode;
  }

  public addUpdateStudyCode(invitation: InvitationInterface, studyCode: string): void {
    if (this.disableUpdateStudyCode(invitation, studyCode)) {
      return;
    }
    if (!studyCode) {
      this.deleteStudyCode(invitation);
      return;
    }

    this.addUpdateStudyCodeResponse.next('LOADING');
    if (this.isManager) {
      this.studyStore.addUpdateMemberCodeEM({
        invitationId: invitation.id,
        codeParam: studyCode,
      });
    } else {
      this.studyStore.addUpdateMemberCode({
        invitationId: invitation.id,
        codeParam: studyCode,
      });
    }
    this.updateMemberCodeResponse$
      .pipe(
        skip(1),
        take(1),
        mergeMap((result: any) => {
          if (result instanceof HttpResponse) {
            return of(result);
          } else {
            return throwError(() => result);
          }
        }),
      )
      .subscribe({
        next: () => {
          this.addUpdateStudyCodeResponse.next('SUCCESS');
          this.emitStudyCodeUpdate.emit(studyCode);
        },
        error: () => {
          this.addUpdateStudyCodeResponse.next('FAILURE');
          setTimeout(() => {
            this.addUpdateStudyCodeResponse.next('DEFAULT');
          }, 2500);
        },
        complete: () => {
          setTimeout(() => {
            this.addUpdateStudyCodeResponse.next('DEFAULT');
          }, 2500);
        },
      });
  }

  disableDeleteStudyCode(invitation: InvitationInterface) {
    return !invitation.attributes.code;
  }

  public deleteStudyCode(invitation: InvitationInterface): void {
    if (this.disableDeleteStudyCode(invitation)) {
      return;
    }

    this.deleteStudyCodeResponse.next('LOADING');
    if (this.isManager) {
      this.studyStore.deleteCodeOfStudyInvitationEM({ invitationId: invitation.id });
    } else {
      this.studyStore.deleteCodeOfStudyInvitation({ invitationId: invitation.id });
    }
    this.deleteMemberCodeResponse$
      .pipe(
        skip(1),
        take(1),
        mergeMap((result: any) => {
          if (result instanceof HttpResponse) {
            return of(result);
          } else {
            return throwError(() => result);
          }
        }),
      )
      .subscribe({
        next: () => {
          this.deleteStudyCodeResponse.next('SUCCESS');
          this.emitStudyCodeUpdate.emit(null);
          this.studyCode = '';
          setTimeout(() => {
            this.deleteStudyCodeResponse.next('DEFAULT');
          }, 2500);
        },
        error: () => {
          this.deleteStudyCodeResponse.next('FAILURE');
          setTimeout(() => {
            this.deleteStudyCodeResponse.next('DEFAULT');
          }, 2500);
        },
      });
  }

  public resendStudyInvitation(studyId: number, email: string, invitation: InvitationInterface): void {
    const payloadStudy = {
      data: {
        type: 'users',
        attributes: {
          users: [{ email }],
        },
      },
    };
    this.resendStudyInvitationResponse.next('LOADING');
    if (this.isManager) {
      this.studyStore.resendStudyInvitationEM({ studyId, invitationsParam: [invitation.id] });
    } else {
      this.studyStore.addMembers({ studyId, payload: payloadStudy });
    }
    this.addMembersResponse$.pipe(skip(1), take(1)).subscribe({
      next: (result) => {
        if (result instanceof HttpResponse) {
          this.resendStudyInvitationResponse.next('SUCCESS');
          this.emitUpdate.emit();
          setTimeout(() => {
            this.resendStudyInvitationResponse.next('DEFAULT');
          }, 2500);
        } else {
          this.resendStudyInvitationResponse.next('FAILURE');
          setTimeout(() => {
            this.resendStudyInvitationResponse.next('DEFAULT');
          }, 2500);
        }
      },
      error: () => {
        this.resendStudyInvitationResponse.next('FAILURE');
        setTimeout(() => {
          this.resendStudyInvitationResponse.next('DEFAULT');
        }, 2500);
      },
    });
  }

  public deleteStudyInvitation(invitationId: number): void {
    this.deleteStudyInvitationResponse.next('LOADING');
    if (this.isManager) {
      this.studyStore.deleteStudyInvitationEM({ invitationId });
    } else {
      this.studyStore.deleteCodeOfStudyInvitation({ invitationId });
    }
    this.deleteStudyInvitationResponse$.pipe(skip(1), take(1)).subscribe({
      next: (result) => {
        if (result instanceof HttpResponse) {
          this.deleteStudyInvitationResponse.next('SUCCESS');
          this.emitDelete.emit();
        } else {
          this.deleteStudyInvitationResponse.next('FAILURE');
        }
        setTimeout(() => {
          this.deleteStudyInvitationResponse.next('DEFAULT');
        }, 2500);
      },
      error: () => {
        this.deleteStudyInvitationResponse.next('FAILURE');
        setTimeout(() => {
          this.deleteStudyInvitationResponse.next('DEFAULT');
        }, 2500);
      },
    });
  }

  public getStudyById(studyId: number): StudyInterface {
    return this.helper.findArrObjById(studyId, this.studies);
  }

  public isExpired(): boolean {
    return this.invitation.attributes.expires_at < this.currentDate;
  }
}
